import { AlertTriangle, ArrowRight, Clock, Ban, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface PeriodCloseRequiredOverlayProps {
  onExit: () => void;
  hasCashRegisterOpen: boolean;
  periodDate: string | null;
  hoursSinceLastActivity: number;
  hasOpenConsumptions: boolean;
  openConsumptionsCount: number;
  canTransferPending: boolean;
}

export function PeriodCloseRequiredOverlay({
  onExit,
  hasCashRegisterOpen,
  periodDate,
  hoursSinceLastActivity,
  hasOpenConsumptions,
  openConsumptionsCount,
  canTransferPending,
}: PeriodCloseRequiredOverlayProps) {
  const navigate = useNavigate();

  const handleGoToCaixa = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(console.error);
    }
    navigate("/dashboard/caixa");
  };

  const handleGoToConsumo = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(console.error);
    }
    navigate("/dashboard/consumo-aberto");
  };

  const formattedDate = periodDate
    ? format(new Date(periodDate + "T12:00:00"), "dd/MM/yyyy", { locale: ptBR })
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm">
      <div className="max-w-md w-full mx-4 text-center space-y-6">
        <div className="flex justify-center">
          <div className="h-20 w-20 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Clock className="h-10 w-10 text-amber-600" />
          </div>
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            Fechamento de Período Necessário
          </h1>

          <p className="text-muted-foreground">
            O período do dia <span className="font-semibold text-foreground">{formattedDate}</span> precisa ser fechado.
            <br />
            <span className="text-sm">
              ({hoursSinceLastActivity.toFixed(0)}h sem movimentação)
            </span>
          </p>

          {hasCashRegisterOpen && (
            <div className="flex items-center justify-center gap-2 text-amber-600 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 mt-4">
              <AlertTriangle className="h-5 w-5 shrink-0" />
              <p className="text-sm font-medium">
                Feche o turno de caixa primeiro para encerrar o período
              </p>
            </div>
          )}

          {hasOpenConsumptions && !canTransferPending && (
            <div className="flex items-center justify-center gap-2 text-destructive bg-destructive/10 rounded-lg p-3 mt-4">
              <Ban className="h-5 w-5 shrink-0" />
              <p className="text-sm font-medium">
                {openConsumptionsCount} consumo(s) aberto(s) precisam ser recebidos antes de fechar o período
              </p>
            </div>
          )}

          {hasOpenConsumptions && canTransferPending && (
            <div className="flex items-center justify-center gap-2 text-blue-600 bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 mt-4">
              <RefreshCw className="h-5 w-5 shrink-0" />
              <p className="text-sm font-medium">
                {openConsumptionsCount} consumo(s) serão transferidos para o próximo turno
              </p>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {hasOpenConsumptions && !canTransferPending && (
            <Button
              size="lg"
              className="gap-2 w-full"
              onClick={handleGoToConsumo}
            >
              Receber Contas Pendentes
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}

          <Button
            size="lg"
            className={`gap-2 w-full ${hasOpenConsumptions && !canTransferPending ? "" : ""}`}
            variant={hasOpenConsumptions && !canTransferPending ? "outline" : "default"}
            onClick={handleGoToCaixa}
          >
            {hasCashRegisterOpen ? "Fechar Caixa" : "Fechar Período"}
            <ArrowRight className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="lg"
            className="w-full"
            onClick={onExit}
          >
            Voltar ao Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
